export type ReceiptPaperSize = 'a4' | 'a5'

const PAPER_WIDTH_MM: Record<ReceiptPaperSize, number> = {
  a4: 210,
  a5: 148
}

const PAPER_HEIGHT_MM: Record<ReceiptPaperSize, number> = {
  a4: 297,
  a5: 210
}

// Room left on iOS for the printer's own (AirPrint) margins, which iOS adds on top of the page.
// iOS saves PDFs with 0.5in (12.7mm) on each side; the rest covers the receipt's own top margin.
const IOS_PAGE_ALLOWANCE_MM = 32

// iOS prints 1 CSS px as 0.8pt (WebKit's 1.25 print shrink factor on 1px = 1pt) instead of 0.75pt.
const IOS_PRINT_PX_RATIO = 0.75 / 0.8

// Tailwind font sizes (rem) used inside the receipts.
const TEXT_CLASS_REM: Record<string, number> = {
  'text-xs': 0.75,
  'text-sm': 0.875,
  'text-base': 1,
  'text-lg': 1.125,
  'text-xl': 1.25,
  'text-2xl': 1.5
}

const PAGE_MARGIN_TOP_PX = 40
const PAGE_MARGIN_BOTTOM_PX = 30

export function receiptPaperSize(size: string | null | undefined): ReceiptPaperSize {
  return size === 'a5' ? 'a5' : 'a4'
}

/**
 * Print stylesheet for the A4/A5 receipt layout.
 *
 * The receipt keeps its A4 layout and is zoomed down to the target paper, so
 * the printed result is the same everywhere instead of depending on the
 * browser's shrink-to-fit (iOS Safari does not shrink, so an unscaled A4
 * layout spills over a second page on A5 paper).
 *
 * `.a4-receipt-page` wrappers are full page height, so their `vh` height is
 * divided by the same scale to stay one printed page once zoomed.
 *
 * iOS/iPadOS (WebKit, matched by `-webkit-touch-callout`) does not resolve that
 * `vh` against the printed page, so the wrapper ends up taller than the paper and
 * the footer spills onto a second page. There the wrapper gets a fixed physical
 * height instead, a bit shorter than the paper to leave room for printer margins.
 * The same `vh` problem makes the hidden app shell (and the `height: 100%`
 * receipt container) taller than one page, which printed a blank extra page, so
 * on iOS everything outside the receipt is removed and its ancestors shrink to fit.
 *
 * iOS also prints 1 CSS px as 0.8pt (desktop: 0.75pt), and its text autosizing
 * renders every font at its specified size, ignoring the zoom. So on iOS the zoom
 * is corrected for the bigger pixel, and the receipt's font sizes are scaled by
 * the same factor directly (root via `em`, Tailwind `text-*` classes, and inline
 * sizes through `--receipt-font-scale`).
 */
export function receiptPrintCSS(size: string | null | undefined, selector = '#a4-receipt'): string {
  const paper = receiptPaperSize(size)
  const scale = PAPER_WIDTH_MM[paper] / PAPER_WIDTH_MM.a4
  const iosZoom = scale * IOS_PRINT_PX_RATIO

  return `
        @media print {
          @page:first {
            size: ${paper.toUpperCase()};
            margin-left: 0;
            margin-right: 0;
            margin-top: 0px;
          }

          @page {
            size: ${paper.toUpperCase()};
            margin-left: 0;
            margin-right: 0;
            margin-top: ${Math.round(PAGE_MARGIN_TOP_PX * scale)}px;
            margin-bottom: ${Math.round(PAGE_MARGIN_BOTTOM_PX * scale)}px;
          }

          ${selector} {
            zoom: ${scale};
          }

          ${selector} .a4-receipt-page {
            height: ${100 / scale}vh !important;
          }

          @supports (-webkit-touch-callout: none) {
            ${selector} {
              zoom: ${iosZoom};
              --receipt-font-scale: ${iosZoom};
            }

            ${selector} > * {
              font-size: ${iosZoom}em;
            }

${iosTextClassCSS(selector, iosZoom)}

            ${selector} .a4-receipt-page {
              height: ${iosPageHeightMM(paper, scale)}mm !important;
            }

            body *:not(${selector}):not(${selector} *):not(:has(${selector})) {
              display: none !important;
            }

            html,
            body,
            body *:has(${selector}),
            ${selector} {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
          }
        }`
}

function iosPageHeightMM(paper: ReceiptPaperSize, scale: number): number {
  return Math.floor((PAPER_HEIGHT_MM[paper] - IOS_PAGE_ALLOWANCE_MM) / scale)
}

function iosTextClassCSS(selector: string, zoom: number): string {
  return Object.entries(TEXT_CLASS_REM)
    .map(([cls, rem]) => `            ${selector} .${cls} { font-size: ${rem * zoom}rem; }`)
    .join('\n')
}
